import { Table, TableBody, TextField, Typography } from '@mui/material';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { L } from 'harmony-language';
import React from 'react';
import { getDaysOfWorkingWeek } from '../../../../../utils/date-time';
import { AgisticsItem, AgisticsSelect } from '../../../../shared/agistics-select';
import { SiteProjection } from '../bulk-drafts-types';

interface BulkEntryTableProps {
    siteSelectItems: AgisticsItem[];
    siteProjections: SiteProjection[];
    setSiteProjections: React.Dispatch<React.SetStateAction<SiteProjection[]>>;
    locationSelectHandler: (id: number) => void;
    isOriginBased: boolean;
}

export const BulkEntryTable = (props: BulkEntryTableProps) => {
    const { siteSelectItems, siteProjections, setSiteProjections, locationSelectHandler, isOriginBased } = props;

    const hasNoApplicableSites = siteSelectItems.length === 0 && siteProjections.length === 0;

    function handleLoadChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, siteIndex: number, weekIndex: number, dayIndex: number) {
        const newValue = parseInt(event.target.value, 10) || 0;
        setSiteProjections(prevProjections => {
            const updatedProjections = [...prevProjections];
            updatedProjections[siteIndex].projectionWeek[weekIndex].schedule[dayIndex] = newValue;
            return updatedProjections;
        });
    }

    return (
        <div className='bulk-entry-table'>
            <Table>
                <TableHead>
                    <TableRow className='bulk-entry-top-header'>
                        <TableCell>{isOriginBased ? L.bulkEntryOrigins(siteProjections.length.toString()) : L.bulkEntryDestinations(siteProjections.length.toString())}</TableCell>
                        <TableCell sx={{ textAlign: 'center'}} className='bulk-entry-group-header' colSpan={7}>{isOriginBased ? L.bulkEntryPerDayOrigin() : L.bulkEntryPerDayDestination()}</TableCell>
                        <TableCell sx={{ textAlign: 'center'}} className='bulk-entry-group-header'>{L.totals()}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {siteProjections.map((projection, siteIndex) =>
                        projection.projectionWeek.map((weekOf, weekIndex) => {
                            const isFirstSiteRow = weekIndex === 0;
                            const isLastSiteRow = weekIndex === projection.projectionWeek.length - 1;
                            const stopTypeLabel = isOriginBased ? L.origin() : L.destination();

                            return (
                                <>
                                    <TableRow>
                                        <TableCell>{isFirstSiteRow && stopTypeLabel}</TableCell>
                                        {getDaysOfWorkingWeek(weekOf.week).map(day => <TableCell key={day.label}>{day.label}</TableCell>)}
                                        <TableCell />
                                    </TableRow>
                                    <TableRow key={siteIndex} className={!isLastSiteRow ? 'bulk-entry-table-site-row' : ''}>
                                        <TableCell>{isFirstSiteRow && projection.orgLocation.name}</TableCell>
                                        {[...Array(7)].map((val, dayIndex) => (
                                            <TableCell key={dayIndex} className='bulk-entry-load-amount'>
                                                <TextField
                                                    variant='standard'
                                                    onChange={event => handleLoadChange(event, siteIndex, weekIndex, dayIndex)}
                                                    value={weekOf.schedule[dayIndex] || ''}
                                                    autoComplete='off'
                                                    autoFocus={siteIndex === siteProjections.length -1 && isFirstSiteRow && dayIndex === 0}
                                                    inputProps={{ maxLength: 3 }} />
                                            </TableCell>
                                        ))}
                                        <TableCell sx={{ textAlign: 'right' }}>{weekOf.schedule.reduce((total, loads) => total + loads, 0)}</TableCell>
                                    </TableRow>
                                </>
                            )
                        })
                    )}
                    <TableRow>
                        <div style={{ padding: '1rem' }}>
                            {hasNoApplicableSites
                                ? <Typography>{L.bulkEntryNoLocations()}</Typography>
                                : siteSelectItems.length > 0 && (
                                    <AgisticsSelect
                                        label={L.addLocation()}
                                        item={-1}
                                        items={siteSelectItems}
                                        blurOnSelect
                                        onChange={locationSelectHandler}
                                        showNone
                                    />
                                )
                            }
                        </div>
                    </TableRow>
                </TableBody>
            </Table>
        </div>
    );
};
